import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  FormHelperText,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSelector } from '../../../redux/store';
import EventsApi from '../../../api/eventsApi/eventsApi';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({ open, onClose }) => {
  const email = useSelector(state => state.auth.email) || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [isValid, setIsValid] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const validatePassword = (password: string) => {
    const minLength = password.length >= 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (!minLength) {
      return 'Şifre en az 8 karakter uzunluğunda olmalıdır';
    }
    if (!hasUpperCase) {
      return 'Şifre en az 1 büyük harf içermelidir';
    }
    if (!hasLowerCase) {
      return 'Şifre en az 1 küçük harf içermelidir';
    }
    if (!hasSpecialChar) {
      return 'Şifre en az 1 özel karakter içermelidir';
    }
    return '';
  };

  useEffect(() => {
    const validationResult = validatePassword(newPassword);
    if (validationResult) {
      setError(validationResult);
      setIsValid(false);
    } else if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor');
      setIsValid(false);
    } else {
      setError('');
      setIsValid(true);
    }
  }, [newPassword, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submit button clicked');

    try {
      const response = await EventsApi.changePassword({
        email,
        newPassword
      });
      console.log('API Response:', response);
      console.log('Şifre başarıyla değiştirildi');
      onClose();
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      setError('Şifre değiştirme işlemi başarısız oldu');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Şifre Değiştir</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            fullWidth
            label="Yeni Şifre"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{ mb: 2 }}
            error={!!error && newPassword.length > 0}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Yeni Şifre Tekrar"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={!!error && confirmPassword.length > 0}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {error && (newPassword.length > 0 || confirmPassword.length > 0) && (
            <FormHelperText error sx={{ mt: 1 }}>
              {error}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>İptal</Button>
          <Button
            sx={{
              backgroundColor: '#094151',
              color: "#fff",
              '&:hover': {
                backgroundColor: '#022631',
                color: "#fff",
                fontWeight: 600,
              },
              '&:disabled': {
                backgroundColor: '#094151',
                opacity: 0.5,
                color: "#fff"
              }
            }}
            type="submit"
            disabled={!isValid}
          >
            Şifre Değiştir
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProfileModal; 